import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ExpertContact from "@/components/ExpertContact";
import * as Icons from "lucide-react";
import { ArrowUpRight, Search, MessageCircle, ChevronLeft } from "lucide-react";

export default function StartNiche() {
  const [niches, setNiches] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const nav = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/niches`)
      .then((r) => {
        setNiches(r.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load niches:", err);
        setError("Failed to load categories. Please refresh the page.");
        setLoading(false);
      });
  }, []);

  const pick = (n) => {
    sessionStorage.setItem("askwinn_funnel_niche", n.key);
    sessionStorage.setItem("askwinn_funnel_niche_label", n.label);
    sessionStorage.setItem("askwinn_pending_sector", n.label);
    if (user?.role === "buyer") {
      nav(`/rfqs/new?sector=${encodeURIComponent(n.label)}`);
    } else {
      localStorage.setItem("askwinn_desired_role", "buyer");
      nav(`/login?redirect=${encodeURIComponent(`/rfqs/new?sector=${n.label}`)}`);
    }
  };

  const filteredNiches = niches.filter(n => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;

    const label = (n.label || n.name || "").toLowerCase();
    if (label.includes(query)) return true;

    if (Array.isArray(n.sub_categories)) {
      return n.sub_categories.some(s => 
        typeof s === "string" && s.toLowerCase().includes(query)
      );
    }
    
    return false;
  });

  if (error) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={() => window.location.reload()} className="btn-primary">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <button 
              onClick={() => nav("/")} 
              className="flex items-center gap-2 text-xs font-mono text-[--muted-foreground] mb-6 hover:text-ink transition-colors group"
            >
              <ChevronLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
              BACK TO HOME
            </button>
            <div className="overline mb-4">§ STEP 01 / 04 — PICK A NICHE</div>
            <h1 className="font-serif text-5xl lg:text-7xl font-light leading-[0.95] tracking-tight mb-6">
              Build your brand.<br />
              Find the right <em className="text-klein not-italic">manufacturer</em>.
            </h1>
            <p className="text-lg text-[--muted-foreground] max-w-2xl">
              Tell us what you're building and we'll match you with verified end-to-end manufacturers.
              Pick a category to start your guided brief.
            </p>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 hidden lg:block">
            <div className="editorial-card p-6 border-l-4 border-l-klein">
              <div className="overline mb-3">§ HOW IT WORKS</div>
              <ol className="space-y-3 text-sm">
                <li className="flex gap-3"><span className="font-mono text-klein shrink-0">01.</span> Pick a niche</li>
                <li className="flex gap-3"><span className="font-mono text-klein shrink-0">02.</span> Answer 3 quick questions</li>
                <li className="flex gap-3"><span className="font-mono text-klein shrink-0">03.</span> Unlock your Blueprint</li>
                <li className="flex gap-3"><span className="font-mono text-klein shrink-0">04.</span> Post your RFQ — manufacturers bid</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="mb-10 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[--muted-foreground]" />
          <input 
            type="text" 
            placeholder="Search for a category (e.g. 'Jewellery' or 'Skincare')..." 
            className="w-full pl-10 pr-4 py-3 bg-white border border-stone-200 rounded-none focus:outline-none focus:border-klein transition-colors font-mono text-sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="font-mono text-sm">Loading categories...</div>
          </div>
        ) : filteredNiches.length === 0 ? (
          <div className="text-center py-20">
            <div className="font-mono text-sm text-[--muted-foreground]">No categories match your search. Try something else.</div>
            <button 
              onClick={() => setSearchQuery("")} 
              className="mt-4 text-xs underline text-klein"
            >
              Clear search
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="niches-grid">
            {filteredNiches.map((n, i) => {
              const Icon = Icons[n.icon] || Icons.Box;
              return (
                <button
                  key={n.key}
                  onClick={() => pick(n)}
                  className="editorial-card p-7 text-left group transition-all duration-300 hover:border-klein"
                  style={{ animation: `reveal 0.5s ease-out ${i * 60}ms both` }}
                  data-testid={`niche-card-${n.key}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <Icon className="w-6 h-6 text-klein" />
                    <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
                  </div>
                  <div className="font-serif text-3xl tracking-tight leading-tight mb-3 group-hover:text-klein transition-colors">{n.label}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-burn">{n.opportunity_teaser || "Exploring opportunities..."}</div>
                  <div className="hairline mt-5 pt-4 text-xs text-[--muted-foreground] flex flex-wrap gap-x-3 gap-y-1">
                    {Array.isArray(n.sub_categories) && n.sub_categories.slice(0, 3).map((s) => (<span key={s}>· {s}</span>))}
                    {Array.isArray(n.sub_categories) && n.sub_categories.length > 3 && <span>· +{n.sub_categories.length - 3} more</span>}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-20 p-8 lg:p-12 bg-stone-100 border border-stone-200 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="max-w-xl text-center md:text-left">
            <h3 className="font-serif text-3xl mb-3">Don't see your category?</h3>
            <p className="text-[--muted-foreground]">
              We're expanding our network daily. If your niche isn't listed, our experts can still help you build your brief and find a verified manufacturer.
            </p>
          </div>
          <ExpertContact>
            <button
              type="button"
              className="btn-primary flex items-center gap-2 px-8 py-4"
              data-testid="other-niche-expert-link"
            >
              <MessageCircle className="w-4 h-4" /> Talk to an expert
            </button>
          </ExpertContact>
        </div>
      </div>
      <Footer />
    </div>
  );
}
